/**
 * Agent List frame — canvas frame showing the project's agent roster.
 *
 * Displays each project agent as a row with colored avatar, name, and status dot.
 */

import { useEffect, useMemo } from "react";
import type { FrameProps } from "@/components/shapes/shared/frame-props";
import { useT } from "@/i18n";
import { framesAgentListMessages } from "@/i18n/messages/frames-agent-list";
import { cn } from "@/lib/cn";
import { useAgentStore } from "@/stores/agent-store";
import { AgentAvatarPreview } from "@/components/ui/agent-avatar-preview";
import { useAuth } from "@/hooks/use-auth";
import { formatModelLabel } from "@/lib/model-registry";
import type { AgentAvatarStyleKey } from "@/lib/dicebear-styles";
import { useFrameSurface } from "../shared/use-frame-surface";

export function AgentListFrame({ id, selected }: FrameProps) {
  const t = useT(framesAgentListMessages);
  const frameSurface = useFrameSurface(id);
  const agents = useAgentStore((s) => s.agents);
  const templates = useAgentStore((s) => s.templates);
  const fetchAllTemplates = useAgentStore((s) => s.fetchAllTemplates);
  const { user } = useAuth();
  const avatarStyleKey = (user?.agent_avatar_style ??
    "glass") as AgentAvatarStyleKey;

  // Fetch templates once if missing — needed to read each agent's model.
  useEffect(() => {
    if (templates.length === 0) fetchAllTemplates();
  }, [templates.length, fetchAllTemplates]);

  // template id → adapter_type + model lookup
  const templatesById = useMemo(() => {
    const m = new Map<
      string,
      { adapter_type: string; model: string | undefined }
    >();
    for (const template of templates) {
      m.set(template.id, {
        adapter_type: template.adapter_type,
        model: template.adapter_config?.model as string | undefined,
      });
    }
    return m;
  }, [templates]);

  return (
    <div
      className={cn(
        frameSurface.className,
        "flex h-full flex-col overflow-hidden",
        selected
          ? "border-[var(--frame-selected-stroke)]"
          : "border-glass-stroke",
      )}
      data-testid="agent-list-frame"
      {...frameSurface.attributes}
    >
      <div
        className={cn(
          "scrollbar-none nodrag flex flex-1 flex-col gap-0.5 p-2 overflow-y-auto",
          selected && "nowheel",
        )}
        data-frame-content="true"
        data-testid="agent-list-frame-body"
      >
        {agents.length === 0 ? (
          <p className="italic text-muted text-canvas-sm px-1">
            {t("noAgentsAssigned")}
          </p>
        ) : (
          agents.map((agent) => {
            const tpl = templatesById.get(agent.agent_template);
            const modelLabel = formatModelLabel(tpl?.adapter_type, tpl?.model);
            return (
              <div
                key={agent.id}
                className="flex items-center gap-2 rounded px-1.5 py-1 hover:bg-glass-fill-heavy transition-colors"
                data-testid={`agent-list-row-${agent.id}`}
              >
                {/* Avatar */}
                <AgentAvatarPreview
                  styleKey={avatarStyleKey}
                  seed={agent.template_avatar_seed || agent.template_name}
                  size={24}
                />

                {/* Name + model */}
                <div className="flex min-w-0 flex-1 flex-col leading-tight">
                  <span
                    className="truncate text-canvas-sm text-secondary"
                    data-testid={`agent-list-name-${agent.id}`}
                  >
                    {agent.template_name}
                  </span>
                  <span
                    className="truncate text-canvas-2xs text-muted"
                    data-testid={`agent-list-model-${agent.id}`}
                  >
                    {modelLabel}
                  </span>
                </div>

                {/* Status dot */}
                <span
                  className={cn(
                    "h-1.5 w-1.5 shrink-0 rounded-full",
                    agent.is_template_builtin ? "bg-accent" : "bg-muted",
                  )}
                  aria-label={
                    agent.is_template_builtin
                      ? t("statusActive")
                      : t("statusAvailable")
                  }
                />
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
